import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Phone, Gift, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PHONE_CAPTURE_DELAY_MS } from "@/lib/constants";
import { devError } from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "was.phoneCapture.v1";

export const PhoneCapture = () => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const flag = localStorage.getItem(STORAGE_KEY);
    if (flag === "dismissed" || flag === "submitted") return undefined;
    const t = setTimeout(() => setOpen(true), PHONE_CAPTURE_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = (reason = "dismissed") => {
    setOpen(false);
    localStorage.setItem(STORAGE_KEY, reason);
  };

  const submit = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/[^\d+]/g, "");
    if (cleaned.replace(/\D/g, "").length < 10) {
      toast.error("Please enter a valid phone number.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/phone`, {
        phone: cleaned,
        source: window.location.pathname,
      });
      toast.success("You're in! Your 10% coupon code WELCOME10 is ready at checkout.");
      close("submitted");
    } catch (err) {
      devError("Phone lead submit failed:", err);
      toast.error("Could not save your phone. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="phone-capture-title"
      className="fixed bottom-4 left-4 z-50 w-[calc(100%-2rem)] max-w-sm border-2 border-slate-900 bg-white shadow-[8px_8px_0_0_rgba(16,185,129,1)] sm:bottom-6 sm:left-6"
      data-testid="phone-capture-modal"
    >
      <button
        onClick={() => close("dismissed")}
        data-testid="phone-capture-close"
        aria-label="Close"
        className="absolute right-2 top-2 p-1 text-slate-500 hover:text-slate-900"
      >
        <X className="h-4 w-4" />
      </button>

      <form onSubmit={submit} className="p-6">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] text-emerald-600">
          <Gift className="h-3 w-3" /> Exclusive 10% off
        </div>
        <h2
          id="phone-capture-title"
          className="mt-2 font-display text-2xl font-black tracking-tighter text-slate-900"
        >
          Get a 10% off code
          <br />
          by SMS.
        </h2>
        <p className="mt-2 text-sm text-slate-600">
          Drop your phone and we&apos;ll text you <span className="font-bold">WELCOME10</span> instantly,
          plus stock alerts and exclusive flash sales.
        </p>

        <div className="mt-4">
          <label htmlFor="phone-input" className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
            Phone number
          </label>
          <div className="mt-1 flex items-center border border-slate-300 focus-within:border-emerald-500 focus-within:ring-2 focus-within:ring-emerald-500">
            <span className="flex items-center gap-1 border-r border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
              <Phone className="h-3.5 w-3.5" /> US
            </span>
            <Input
              id="phone-input"
              type="tel"
              data-testid="phone-capture-input"
              placeholder="+1 (404) 555-0123"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="border-0 shadow-none focus-visible:ring-0"
              autoComplete="tel"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={submitting}
          data-testid="phone-capture-submit"
          className="mt-4 w-full rounded-sm bg-slate-900 py-5 font-bold text-white hover:bg-slate-800 disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Text me my 10% code"}
        </Button>

        <div className="mt-3 flex items-center gap-1.5 text-[10px] text-slate-500">
          <ShieldCheck className="h-3 w-3 text-emerald-500" />
          We&apos;ll never share your number. Unsubscribe any time.
        </div>
      </form>
    </div>
  );
};
